import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shirt, Star, Filter, Search, ChevronRight, ShoppingBag, 
  Sparkles, Crown, Heart, Grid, LayoutGrid, X, Truck,
  MapPin, Package, ChevronLeft, Play, Pause
} from 'lucide-react';
import { toast } from 'sonner';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { InlineCostumeField } from '@/components/edit/InlineCostumeField';
import { InlineCostumeImage } from '@/components/edit/InlineCostumeImage';const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero slider images from the original WordPress site (in exact sequence)
const HERO_SLIDER_IMAGES = [
  { id: 1, url: "/api/media/casa-samba/library/images/costume-shop/hero-1.jpg" },
  { id: 2, url: "/api/media/casa-samba/library/images/costume-shop/hero-2.jpg" },
  { id: 3, url: "/api/media/casa-samba/library/images/costume-shop/hero-3.jpg" },
  { id: 4, url: "/api/media/casa-samba/library/images/costume-shop/hero-4.jpg" },
  { id: 5, url: "/api/media/casa-samba/library/images/costume-shop/hero-5.jpg" },
  { id: 6, url: "/api/media/casa-samba/library/images/costume-shop/hero-6.jpg" },
  { id: 7, url: "/api/media/casa-samba/library/images/costume-shop/hero-7.jpg" },
  { id: 8, url: "/api/media/casa-samba/library/images/costume-shop/hero-8.jpg" },
  { id: 9, url: "/api/media/casa-samba/library/images/costume-shop/hero-9.jpg" },
  { id: 10, url: "/api/media/casa-samba/library/images/costume-shop/hero-10.jpg" },
  { id: 11, url: "/api/media/casa-samba/library/images/costume-shop/hero-11.jpg" },
  { id: 12, url: "/api/media/casa-samba/library/images/costume-shop/hero-12.jpg" },
  { id: 13, url: "/api/media/casa-samba/library/images/costume-shop/hero-13.jpg" },
];

function CostumeShopInner() {
  const [searchParams] = useSearchParams();
  const { getValue, enabled } = usePageEditMode();
  const [costumes, setCostumes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  // Hero slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance slider
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDER_IMAGES.length);
    }, 4000); // Change every 4 seconds
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % HERO_SLIDER_IMAGES.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + HERO_SLIDER_IMAGES.length) % HERO_SLIDER_IMAGES.length);
  }, []);

  useEffect(() => {
    loadData();
    if (searchParams.get('cancelled')) {
      toast.error('Checkout was cancelled');
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [costumesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/costumes`),
        axios.get(`${API}/costumes/categories`)
      ]);
      setCostumes(costumesRes.data.costumes || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error loading costumes:', error);
      toast.error('Failed to load costumes');
    } finally {
      setLoading(false);
    }
  };

  const handleCostumeSaved = (updated) => {
    setCostumes((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
  };

  // Filter costumes
  const filteredCostumes = costumes.filter(costume => {
    const matchesSearch = !searchQuery || 
      costume.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      costume.description?.toLowerCase().includes(searchQuery.toLowerCase());    const matchesCategory = activeCategory === 'all' || costume.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="costume-shop-loading">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" data-testid="costume-shop-page">
      {/* Fullwidth Hero Slider */}
      <section className="relative w-full h-[50vh] md:h-[60vh] lg:h-[70vh] overflow-hidden" data-testid="hero-slider">
        {/* Slider Images */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <img
              src={HERO_SLIDER_IMAGES[currentSlide].url}
              alt={`Carnival Costume Showcase ${currentSlide + 1}`}
              className="w-full h-full object-cover"
              loading={currentSlide === 0 ? "eager" : "lazy"}
            />
            {/* Gradient overlays for depth and text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full text-white transition-all hover:scale-110 group"
          data-testid="slider-prev"
        >
          <ChevronLeft className="w-6 h-6 group-hover:-translate-x-0.5 transition-transform" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full text-white transition-all hover:scale-110 group"
          data-testid="slider-next"
        >
          <ChevronRight className="w-6 h-6 group-hover:translate-x-0.5 transition-transform" />
        </button>

        {/* Play/Pause Button */}
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="absolute top-4 right-4 z-20 p-2 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full text-white transition-all"
          data-testid="slider-autoplay-toggle"
        >
          {isAutoPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>

        {/* Slide Indicators (Dots) */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {HERO_SLIDER_IMAGES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                idx === currentSlide 
                  ? 'w-8 bg-primary' 
                  : 'bg-white/50 hover:bg-white/80'
              }`}
              data-testid={`slider-dot-${idx}`}
            />
          ))}
        </div>

        {/* Hero Content Overlay */}
        <div className="absolute inset-0 z-10 flex items-end">
          <div className="w-full px-6 pb-20 md:pb-24">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/30 backdrop-blur-sm border border-primary/40 mb-4">
                  <Crown className="w-5 h-5 text-primary" />
                  <span className="text-sm font-bold text-white uppercase tracking-wider">
                    <PageEditable path="hero.eyebrow" fallback="33 Years of Experience" />
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-white mb-3 drop-shadow-lg">
                  <PageEditable path="hero.title" fallback="Mestre Curtis Pierre's" /> <br />
                  <span className="text-primary"><PageEditable path="hero.titleAccent" fallback="Costume Shop" /></span>
                </h1>
                <PageEditable
                  path="hero.subtitle"
                  as="p"
                  multiline
                  className="text-lg text-white/90 max-w-xl mb-6 drop-shadow-md"
                  fallback="Authentic Brasilian carnival costumes handcrafted in New Orleans. From traditional Bahiana dresses to elaborate Passista outfits."
                />

                {/* Feature Pills */}
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 px-4 py-2 bg-black/40 backdrop-blur-sm rounded-full border border-white/20">
                    <Truck className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-white">
                      <PageEditable path="hero.pill1" fallback="Nationwide Shipping" />
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-black/40 backdrop-blur-sm rounded-full border border-white/20">
                    <MapPin className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-white">
                      <PageEditable path="hero.pill2" fallback="Local Pickup NOLA" />
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-black/40 backdrop-blur-sm rounded-full border border-white/20">
                    <Package className="w-4 h-4 text-orange-400" />
                    <span className="text-sm text-white">
                      <PageEditable path="hero.pill3" fallback="Custom Orders" />
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Bar Section */}
      <section className="relative -mt-8 z-30 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder={getValue('search.placeholder') || 'Search costumes...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-14 pr-6 py-5 bg-card border border-border/50 rounded-2xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 shadow-2xl"
              data-testid="costume-search-input"
            />
          </div>
          {enabled && (
            <div className="mt-2 text-xs text-muted-foreground">
              Placeholder: <PageEditable path="search.placeholder" fallback="Search costumes..." />
            </div>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        {/* Category Filter Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          {/* Categories */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                activeCategory === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-stone-800 text-muted-foreground hover:bg-stone-700'
              }`}
              data-testid="category-all"
            >
              <PageEditable path="categories.allLabel" fallback="All Costumes" />
            </button>
            {categories
              .filter((cat) => costumes.some((c) => c.category === (cat.slug || cat.id)))
              .map((cat) => (
              <button
                key={cat.id || cat.slug}
                onClick={() => setActiveCategory(cat.slug || cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeCategory === (cat.slug || cat.id)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-stone-800 text-muted-foreground hover:bg-stone-700'
                }`}
                data-testid={`category-${cat.slug || cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-stone-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="view-grid"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="view-list"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Results Count */}
        <p className="text-sm text-muted-foreground mb-6">
          <PageEditable path="results.prefix" fallback="Showing" />{' '}
          {filteredCostumes.length}{' '}
          <PageEditable
            path={filteredCostumes.length === 1 ? 'results.singular' : 'results.plural'}
            fallback={filteredCostumes.length === 1 ? 'costume' : 'costumes'}
          />
        </p>

        {/* Costumes Grid */}
        {filteredCostumes.length === 0 ? (
          <div className="text-center py-20" data-testid="no-costumes">
            <Shirt className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">
              <PageEditable path="empty.title" fallback="No Costumes Found" />
            </h3>
            <p className="text-muted-foreground mb-6">
              <PageEditable
                path={searchQuery ? 'empty.searching' : 'empty.default'}
                fallback={searchQuery ? 'Try adjusting your search or filters' : 'Check back soon for new arrivals!'}
              />
            </p>
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('all');
                }}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all"
              >
                <PageEditable path="empty.clearButton" fallback="Clear Filters" />
              </button>
            )}
          </div>
        ) : (
          <motion.div
            layout
            className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'flex flex-col gap-4'
            }
            data-testid="costumes-grid"
          >
            <AnimatePresence>
              {filteredCostumes.map((costume, index) => (
                <CostumeCard 
                  key={costume.id} 
                  costume={costume} 
                  index={index}
                  viewMode={viewMode}
                  onSaved={handleCostumeSaved}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* CTA Section */}
        <section className="mt-20 relative overflow-hidden rounded-3xl bg-gradient-to-r from-amber-900/40 via-stone-900 to-orange-900/30 border border-amber-500/20 p-12">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500 rounded-full blur-3xl" />
          </div>
          <div className="relative text-center max-w-2xl mx-auto">
            <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-3xl font-bold font-display text-foreground mb-4">
              <PageEditable path="cta.title" fallback="Need a Custom Costume?" />
            </h2>
            <PageEditable
              path="cta.subtitle"
              as="p"
              multiline
              className="text-muted-foreground mb-8"
              fallback="Looking for something unique? Mestre Curtis Pierre can create a custom costume tailored to your exact measurements and design preferences."
            />
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-bold hover:bg-primary/90 transition-all hover:scale-105"
              data-testid="custom-order-cta"
            >
              <PageEditable path="cta.buttonText" fallback="Request Custom Order" />
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

// Costume Card Component
const CostumeCard = ({ costume, index, viewMode, onSaved }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const { getValue, enabled } = usePageEditMode();

  const thumbnail = costume.thumbnail || costume.images_thumb?.[0] || costume.images_display?.[0] || costume.images?.[0] || '/api/placeholder/400/500';
  const hasMultipleImages = costume.images?.length > 1;

  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <Link
          to={`/costume-shop/${costume.id}`}
          className="group flex gap-6 bg-card border border-border/50 rounded-2xl p-4 hover:border-primary/50 transition-all duration-300"
          data-testid={`costume-card-${costume.id}`}
        >
          {/* Image */}
          <InlineCostumeImage
            costume={costume}
            src={thumbnail}
            alt={costume.name}
            className="relative w-32 h-40 flex-shrink-0 rounded-xl overflow-hidden bg-stone-800"
            imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onSaved={onSaved}
          />

          {/* Content */}
          <div className="flex-1 flex flex-col justify-between py-1">
            <div>
              <InlineCostumeField
                costume={costume}
                field="category"
                as="span"
                className="text-xs font-semibold text-primary uppercase tracking-wider"
                onSaved={onSaved}
              />
              <InlineCostumeField
                costume={costume}
                field="name"
                as="h3"
                className="text-lg font-bold text-foreground mt-1 group-hover:text-primary transition-colors"
                onSaved={onSaved}
              />
              <InlineCostumeField
                costume={costume}
                field="description"
                as="p"
                className="text-sm text-muted-foreground line-clamp-2 mt-1"
                onSaved={onSaved}
              />
            </div>
            <div className="flex items-center justify-between">
              <InlineCostumeField
                costume={costume}
                field="base_price"
                as="span"
                className="text-xl font-bold text-primary font-display"
                formatter={(v) => `$${Number(v || 0).toFixed(2)}`}
                parser={(t) => Number(String(t).replace(/[^0-9.\-]/g, '')) || 0}
                onSaved={onSaved}
              />
              {costume.sizes_available?.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {costume.sizes_available.length} <PageEditable path="card.sizesLabel" fallback="sizes available" />
                </span>
              )}
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      layout
    >
      <Link
        to={`/costume-shop/${costume.id}`}
        className="group relative block overflow-hidden bg-card border border-border/50 rounded-2xl hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={`costume-card-${costume.id}`}
      >
        {/* Image Container */}
        <div className="relative aspect-[3/4] overflow-hidden bg-stone-800">
          <InlineCostumeImage
            costume={costume}
            src={thumbnail}
            alt={costume.name}
            className="absolute inset-0"
            imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            onSaved={onSaved}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Featured Badge */}
          {costume.featured && (
            <div className="absolute top-3 left-3 flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-bold">
              <Star className="w-3 h-3" />
              <PageEditable path="card.featuredLabel" fallback="Featured" />
            </div>
          )}

          {/* Favorite Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              setIsFavorite(!isFavorite);
              toast.success(isFavorite
                ? (getValue('card.unfavedToast') || 'Removed from favorites')
                : (getValue('card.favedToast') || 'Added to favorites'));
            }}
            className="absolute top-3 right-3 p-2 bg-black/50 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-black/70"
            data-testid={`favorite-btn-${costume.id}`}
          >
            <Heart 
              className={`w-4 h-4 transition-colors ${isFavorite ? 'text-red-500 fill-red-500' : 'text-white'}`} 
            />
          </button>

          {/* Quick View Button */}
          <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all transform translate-y-4 group-hover:translate-y-0">
            <span className="flex items-center justify-center gap-2 w-full py-3 bg-primary text-primary-foreground rounded-full font-semibold text-sm">
              <ShoppingBag className="w-4 h-4" />
              <PageEditable path="card.viewDetails" fallback="View Details" />
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Category Tag */}
          <InlineCostumeField
            costume={costume}
            field="category"
            as="span"
            className="text-xs font-semibold text-primary uppercase tracking-wider"
            onSaved={onSaved}
          />

          {/* Title */}
          <InlineCostumeField
            costume={costume}
            field="name"
            as="h3"
            className="text-lg font-bold text-foreground mt-1 group-hover:text-primary transition-colors line-clamp-1"
            onSaved={onSaved}
          />

          {/* Price & Sizes */}
          <div className="flex items-center justify-between mt-3">
            <InlineCostumeField
              costume={costume}
              field="base_price"
              as="span"
              className="text-xl font-bold text-primary font-display"
              formatter={(v) => `$${Number(v || 0).toFixed(2)}`}
              parser={(t) => Number(String(t).replace(/[^0-9.\-]/g, '')) || 0}
              onSaved={onSaved}
            />
            
            {costume.sizes_available?.length > 0 && (
              <div className="flex gap-1">
                {costume.sizes_available.slice(0, 4).map((size) => (
                  <span 
                    key={size} 
                    className="w-7 h-7 flex items-center justify-center text-xs bg-stone-800 text-muted-foreground rounded"
                  >
                    {size}
                  </span>
                ))}
                {costume.sizes_available.length > 4 && (
                  <span className="w-7 h-7 flex items-center justify-center text-xs bg-stone-700 text-muted-foreground rounded">
                    +{costume.sizes_available.length - 4}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};


import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const CostumeShop = () => (
  <PageEditProvider slug="costume-shop">
    <CostumeShopInner />
    <PageEditToolbar />
    <AdminEditorLink to="/admin/costumes" label="Edit via Dashboard" />
  </PageEditProvider>
);

export { CostumeShop };
export default CostumeShop;
